import React from 'react';

import { cssMap, cx } from '@compiled/react';

import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type HeaderElement,
	isHeaderElement,
	type ParsedElement,
	type SectionElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import { DynamicLayoutFoldableSection } from './dynamic-layout-foldable-section';
import { OneColumnLayout } from './section-layout/one-column-layout';
import { TwoColumnLayout } from './section-layout/two-column-layout';
import { TwoRowLayout } from './section-layout/two-row-layout';

const styles = cssMap({
	overviewStyle: {
		borderRadius: token('radius.small'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral'),
	},
});

export const DynamicLayoutSections = ({
	section,
}: {
	section: SectionElement;
}): React.JSX.Element | null => {
	const layout = section.attributes?.layout || 'one-column';
	const isOverview = section.attributes?.overview === '1';
	const isFoldable = section.attributes?.hasOwnProperty('folded');

	let header: HeaderElement | undefined;
	let content: ParsedElement[] | undefined;
	if (Array.isArray(section.children)) {
		const headerElement = section.children.filter((element) => isHeaderElement(element));
		if (headerElement.length) {
			header = headerElement[0] as HeaderElement;
		}
		content = section.children?.filter((element) => !isHeaderElement(element));
	}

	if (!content) {
		return null;
	}

	const renderContent = () => {
		switch (layout) {
			case 'one-column':
				return <OneColumnLayout elements={content} />;
			case 'two-columns':
				return <TwoColumnLayout elements={content} key={section.attributes?.n} />;
			case 'two-rows':
				return <TwoRowLayout elements={content} key={section.attributes?.n} />;
		}
	};

	if (isFoldable) {
		return (
			<DynamicLayoutFoldableSection section={section} header={header} content={content}>
				{renderContent()}
			</DynamicLayoutFoldableSection>
		);
	}

	return (
		<Stack
			space="space.100"
			alignInline="start"
			xcss={cx(isOverview && styles.overviewStyle)}
			testId="dynamic-layout-section"
			data-layout={layout}
			data-overview={isOverview}
		>
			{header && header.attributes?.show !== 'false' && content && (
				<Inline space="space.100" alignBlock="center">
					{isOverview && <AiGenerativeTextSummaryIcon label="AI Content" size="medium" />}
					<Text weight="bold">{header && (header.children as string)}</Text>
				</Inline>
			)}
			{renderContent()}
		</Stack>
	);
};
